import React, { useEffect, useMemo } from 'react';
import { Button, Card, Checkbox, Form, Select, Space, Typography } from 'antd';
import type { FormInstance } from 'antd/es/form';
import { MinusCircleOutlined } from '@ant-design/icons';
import type { Barna } from '../api/barnaApi';
import type { Bhed } from '../api/bhedApi';
import {
  BHED_FILTER_OPTIONS,
  EMPTY_BHED_FILTERS,
  bhedIdsForBarnaWithFilters,
  normalizeBhedFilters,
  type BhedAttributeFilters,
} from '../utils/bhedAttributeFilters';

export type AssociatedRowForm = {
  barna_id?: number;
  included_bhedas?: number[];
  bhed_filters?: BhedAttributeFilters;
};

export type AssociatedBarnaFormSlice = {
  associated_barnas: AssociatedRowForm[];
};

type BarnaSelectOption = { value: number; label: string };
type BarnaSelectOptionGroup = { label: string; options: BarnaSelectOption[] };

function flattenBarnaSelectOptions(opts: BarnaSelectOptionGroup[]): BarnaSelectOption[] {
  return opts.flatMap((g) => g.options);
}

const FILTER_GROUPS: { key: 'matra' | 'anunasik' | 'swor'; label: string }[] = [
  { key: 'matra', label: 'Matra' },
  { key: 'anunasik', label: 'Anunasik' },
  { key: 'swor', label: 'Swor' },
];

type AssociatedBarnaRowFieldsProps = {
  field: { key: React.Key; name: number };
  /** Parent form (Barna Samuha, Pratyahar, etc.) with `associated_barnas` list */
  form: FormInstance<AssociatedBarnaFormSlice>;
  associatedBarnaOptions: BarnaSelectOptionGroup[];
  barnaRows: Barna[];
  bhedRows: Bhed[];
  onRemove: () => void;
};

export function AssociatedBarnaRowFields({
  field,
  form,
  associatedBarnaOptions,
  barnaRows,
  bhedRows,
  onRemove,
}: AssociatedBarnaRowFieldsProps) {
  const barnaId = Form.useWatch(['associated_barnas', field.name, 'barna_id'], form) as number | undefined;

  const flatBarnaOptions = useMemo(
    () => flattenBarnaSelectOptions(associatedBarnaOptions),
    [associatedBarnaOptions]
  );

  const barnaSelectOptions = useMemo(() => {
    const id = barnaId == null ? null : Number(barnaId);
    if (id == null || Number.isNaN(id)) return associatedBarnaOptions;
    if (flatBarnaOptions.some((o) => o.value === id)) return associatedBarnaOptions;
    const b = barnaRows.find((x) => x.id === id);
    if (!b) return associatedBarnaOptions;
    return [
      {
        label: 'Other',
        options: [
          {
            value: b.id,
            label: `${b.name} (#${b.id}) — ${b.type} (not swar or byanjan; pick another)`,
          },
        ],
      },
      ...associatedBarnaOptions,
    ];
  }, [barnaId, associatedBarnaOptions, flatBarnaOptions, barnaRows]);

  const includedRaw = Form.useWatch(['associated_barnas', field.name, 'included_bhedas'], form);
  const selectedIds = useMemo(() => {
    if (!Array.isArray(includedRaw)) return [] as number[];
    return includedRaw
      .map((x) => (typeof x === 'number' ? x : Number(x)))
      .filter((n): n is number => Number.isFinite(n));
  }, [includedRaw]);

  const barnaNum = barnaId == null || Number.isNaN(Number(barnaId)) ? null : Number(barnaId);
  const selectedBarna = barnaNum == null ? null : barnaRows.find((x) => x.id === barnaNum) ?? null;
  const isByanjan = selectedBarna?.type === 'byanjan';

  const applyFiltersToIncludedBhedas = (nextFilters: BhedAttributeFilters) => {
    if (barnaNum == null) return;
    const ids = bhedIdsForBarnaWithFilters(bhedRows, barnaNum, nextFilters);
    form.setFieldValue(['associated_barnas', field.name, 'included_bhedas'], ids);
  };

  const syncIncludedBhedasFromFormFilters = () => {
    queueMicrotask(() => {
      const current = normalizeBhedFilters(
        form.getFieldValue(['associated_barnas', field.name, 'bhed_filters'])
      );
      applyFiltersToIncludedBhedas(current);
    });
  };

  useEffect(() => {
    if (!isByanjan || selectedIds.length === 0) return;
    form.setFieldValue(['associated_barnas', field.name, 'included_bhedas'], []);
  }, [isByanjan, selectedIds.length, form, field.name]);

  const bhedOptions = useMemo(() => {
    if (barnaNum == null) return [];
    const fromBarna = bhedRows
      .filter((h) => Number(h.barnaId) === barnaNum)
      .map((h) => ({
        value: h.id,
        label: `${h.text} (#${h.id}) — ${h.matra}, ${h.swor}, ${h.anunasik}`,
      }));
    const byValue = new Map(fromBarna.map((o) => [o.value, o]));
    for (const id of selectedIds) {
      if (byValue.has(id)) continue;
      const h = bhedRows.find((x) => x.id === id);
      byValue.set(id, {
        value: id,
        label: h ? `${h.text} (#${id}) — ${h.matra}, ${h.swor}, ${h.anunasik}` : `Bhed #${id}`,
      });
    }
    return Array.from(byValue.values()).sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
    );
  }, [barnaNum, bhedRows, selectedIds]);

  return (
    <Card size="small" style={{ marginBottom: 12 }} styles={{ body: { paddingBottom: 8 } }}>
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
          <Typography.Text type="secondary">Barna & bhedas</Typography.Text>
          <Button type="link" danger size="small" icon={<MinusCircleOutlined />} onClick={onRemove}>
            Remove row
          </Button>
        </Space>
        <Form.Item
          name={[field.name, 'barna_id']}
          label="Barna"
          rules={[{ required: true, message: 'Select a barna' }]}
        >
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Select barna"
            options={barnaSelectOptions}
            onChange={(newBarna) => {
              const nextNum = newBarna == null || newBarna === '' ? null : Number(newBarna);
              queueMicrotask(() => {
                form.setFieldValue(['associated_barnas', field.name, 'bhed_filters'], {
                  ...EMPTY_BHED_FILTERS,
                });
                if (nextNum == null || Number.isNaN(nextNum)) {
                  form.setFieldValue(['associated_barnas', field.name, 'included_bhedas'], []);
                  return;
                }
                const barna = barnaRows.find((x) => x.id === nextNum);
                if (barna?.type === 'byanjan') {
                  form.setFieldValue(['associated_barnas', field.name, 'included_bhedas'], []);
                  return;
                }
                const allIds = bhedIdsForBarnaWithFilters(bhedRows, nextNum, EMPTY_BHED_FILTERS);
                form.setFieldValue(['associated_barnas', field.name, 'included_bhedas'], allIds);
              });
            }}
          />
        </Form.Item>
        {barnaNum != null && !isByanjan ? (
          <div>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              Bhed filters — combine across Matra, Anunasik, and Swor (all checked groups must match)
            </Typography.Text>
            {FILTER_GROUPS.map((group) => (
              <div key={group.key} style={{ marginBottom: 8 }}>
                <Typography.Text style={{ fontSize: 12, marginRight: 8 }}>{group.label}:</Typography.Text>
                <Space wrap size={[8, 4]}>
                  {BHED_FILTER_OPTIONS.filter((o) => o.group === group.key).map((opt) => (
                    <Form.Item
                      key={opt.key}
                      name={[field.name, 'bhed_filters', opt.key]}
                      valuePropName="checked"
                      noStyle
                    >
                      <Checkbox onChange={syncIncludedBhedasFromFormFilters}>{opt.label}</Checkbox>
                    </Form.Item>
                  ))}
                </Space>
              </div>
            ))}
          </div>
        ) : null}
        <Form.Item
          name={[field.name, 'included_bhedas']}
          label="Included bhedas"
          normalize={(v) => (Array.isArray(v) ? v.map((x) => Number(x)).filter((n) => Number.isFinite(n)) : [])}
        >
          <Select
            mode="multiple"
            showSearch
            optionFilterProp="label"
            placeholder={
              barnaNum == null
                ? 'Select a barna first'
                : isByanjan
                  ? 'Not applicable for byanjan'
                  : 'All bheds selected by default; use filters or × to adjust'
            }
            options={bhedOptions}
            disabled={barnaNum == null || isByanjan}
          />
        </Form.Item>
      </Space>
    </Card>
  );
}
